import { isThisYear } from 'date-fns';
import { createTitle } from '#component/shipment/form/cargo/cargo-item/helper';
import { momentFormat } from '#helper';
import { shipmentEventType } from '#type';

export const removeFalsy = obj => {
  const newObj = {};
  Object.keys(obj).forEach(key => {
    if (obj[key]) { newObj[key] = obj[key]; }
  });
  return newObj;
};

/**
 * Returns object based on url search params.
 * @param {string} queryString - search query.
 * @returns {Object} Search query state object.
 */

export const parseQueryState = queryString => {
  const params = new URLSearchParams(queryString);

  return Object.fromEntries(params.entries());
};

/**
 * Build search params
 * @param {string} params - current search params.
 * @param {Array|Object} newParams - new search params.
 * @returns {string} - merged search params
 */

export const getSearchParams = (params, newParams) => {
  let searchParams;

  if (!newParams) {
    return '';
  }

  if (Array.isArray(newParams)) {
    const parsedCurrUrl = parseQueryState(params);
    searchParams = new URLSearchParams({
      ...parsedCurrUrl,
      ...{ project: '', shipment: '', user: '', device: '', event: '' }
    });
    const param = {
      project: [],
      shipment: [],
      user: [],
      device: [],
      page: []
    };
    newParams.forEach(el => {
      const { type, value } = el;
      if (searchParams.has(type)) {
        param[type].push(value);
        searchParams.set(type, `${param[type].join(',')}`);
      } else {
        searchParams.set(type, value);
      }
    });
  } else {
    searchParams = new URLSearchParams(params);
    const { filterType, value, end, start } = newParams;

    if (filterType && value) {
      searchParams.set(filterType, value);
    }
    if (start && end) {
      searchParams.set('start', start);
      searchParams.set('end', end);
    }
  }

  const currentUrl = parseQueryState(params);
  const newUrl = parseQueryState(searchParams.toString());
  const mergedUrls = { ...currentUrl, ...newUrl };
  const url = new URLSearchParams(removeFalsy(mergedUrls));

  return `${url.toString()}`;
};

export const getEventDescription = (item, account, isCurrentShipment) => {
  const {
    actor,
    shipment,
    device,
    delivery,
    cargo,
    file,
    alert,
    event: { id },
    notification = {},
    data
  } = item;

  const {
    CREATE_SHIPMENT,
    EDIT_SHIPMENT,
    DELETE_SHIPMENT,
    STOP_SHIPMENT,
    DONE_SHIPMENT,
    CREATE_DEEPLINK,
    NOT_IN_TIME_SHIPMENT,
    ADD_DEVICE,
    REMOVE_DEVICE,
    ADD_DELIVERY,
    EDIT_DELIVERY,
    REMOVE_DELIVERY,
    DONE_CHECKPOINT,
    NOT_IN_TIME_CHECKPOINT,
    ADD_CARGO,
    EDIT_CARGO,
    REMOVE_CARGO,
    UPLOAD_FILE,
    REMOVE_FILE,
    ADD_ALERT,
    REMOVE_ALERT,
    TRIGGER_ALERT,
    ASSIGN_ALERT_TO_DEVICE,
    UNASSIGN_ALERT_FROM_DEVICE,
    ADD_NOTIFICATION,
    EDIT_NOTIFICATION,
    REMOVE_NOTIFICATION,
    ADD_ALERT_CONFIRMATION,
    EDIT_ALERT_CONFIRMATION,
    REMOVE_ALERT_CONFIRMATION
  } = shipmentEventType;

  try {
    const isAdminAccess = account?.userType < 10;
    const firstAndLastName = (actor && isAdminAccess) ? ` by ${actor.firstName} ${actor.lastName}` : '';
    const cargoTitle = `${createTitle(cargo?.goods, cargo?.container).trim()}`;
    const shipmentLabel = isCurrentShipment ? '' : `" ${shipment.label}"`;
    const recipientObj = notification.addressbookContact || notification.user;
    const recipientName = recipientObj ? `"${recipientObj.firstName} ${recipientObj.lastName}"` : `"${notification.email}"`;
    const alertConfirmationLabel = data?.value;
    const alertLabel = data?.alert?.label;
    const alertTimestamp = data?.mqttMessageTimestamp ?
      momentFormat(data?.mqttMessageTimestamp, account, isThisYear(new Date(data?.mqttMessageTimestamp)) ? 'dateTimePartial' : 'dateTimeShort') : '';

    switch (id) {
      case CREATE_SHIPMENT:
        return `Shipment${shipmentLabel} was created${firstAndLastName}`;
      case EDIT_SHIPMENT:
        return `Shipment${shipmentLabel} was edited${firstAndLastName}`;
      case DELETE_SHIPMENT:
        return `Shipment${shipmentLabel} was removed${firstAndLastName}`;
      case STOP_SHIPMENT:
        return `Shipment${shipmentLabel} was stopped${firstAndLastName}`;
      case DONE_SHIPMENT:
        return `Shipment${shipmentLabel} was completed`;
      case CREATE_DEEPLINK:
        return `Deeplink was created for shipment${shipmentLabel}${firstAndLastName}`;
      case NOT_IN_TIME_SHIPMENT:
        return `Shipment${shipmentLabel} was not completed in time`;
      case ADD_DEVICE:
        return `Device ${device.title || device.uid} was added to shipment${shipmentLabel}${firstAndLastName}`;
      case REMOVE_DEVICE:
        return `Device ${device.title || device.uid} was removed from shipment${shipmentLabel}${firstAndLastName}`;
      case ADD_DELIVERY:
        return `Delivery checkpoint #${delivery.index + 1} was added to shipment${shipmentLabel}${firstAndLastName}`;
      case EDIT_DELIVERY:
        return `Delivery checkpoint #${delivery.index + 1} was edited in shipment${shipmentLabel}${firstAndLastName}`;
      case REMOVE_DELIVERY:
        return `Delivery checkpoint #${delivery.index + 1} was removed from shipment${shipmentLabel}${firstAndLastName}`;
      case DONE_CHECKPOINT:
        return `Delivery checkpoint #${delivery.index + 1} was completed in shipment${shipmentLabel}`;
      case NOT_IN_TIME_CHECKPOINT:
        return `Delivery checkpoint #${delivery.index + 1} was not completed in shipment${shipmentLabel}`;
      case ADD_CARGO:
        return `Cargo ${cargoTitle && `"${cargoTitle}"`} was added to shipment${shipmentLabel}${firstAndLastName}`;
      case EDIT_CARGO:
        return `Cargo ${cargoTitle && `"${cargoTitle}"`} was edited in shipment${shipmentLabel}${firstAndLastName}`;
      case REMOVE_CARGO:
        return `Cargo ${cargoTitle && `"${cargoTitle}"`} was removed from shipment${shipmentLabel}${firstAndLastName}`;
      case UPLOAD_FILE:
        return `File "${file.name}" was uploaded to shipment${shipmentLabel}${firstAndLastName}`;
      case REMOVE_FILE:
        return `File "${file.name}" was removed from shipment${shipmentLabel}${firstAndLastName}`;
      case ADD_ALERT:
        return `Alert "${alert.label}" was added to shipment${shipmentLabel}${firstAndLastName}`;
      case REMOVE_ALERT:
        return `Alert "${alert.label}" was removed from shipment${shipmentLabel}${firstAndLastName}`;
      case TRIGGER_ALERT:
        return `Alert "${alert.label}" was triggered ${shipmentLabel && `in shipment${shipmentLabel}`} by ${device.title || device.uid}`;
      case ASSIGN_ALERT_TO_DEVICE:
        return `Alert "${alert.label}" was assigned to device ${device.title || device.uid} in shipment${shipmentLabel}`;
      case UNASSIGN_ALERT_FROM_DEVICE:
        return `Alert "${alert.label}" was unassigned from device ${device.title || device.uid} in shipment${shipmentLabel}`;
      case ADD_NOTIFICATION:
        return `Recipient ${recipientName} was added to shipment${shipmentLabel}${firstAndLastName}`;
      case EDIT_NOTIFICATION:
        return `Recipient ${recipientName} was changed in shipment${shipmentLabel}${firstAndLastName}`;
      case REMOVE_NOTIFICATION:
        return `Recipient ${recipientName} was removed from shipment in shipment${shipmentLabel}${firstAndLastName}`;
      case ADD_ALERT_CONFIRMATION:
        return `Verification "${alertConfirmationLabel}" was added for alert "${alertLabel}" triggered on ${alertTimestamp}`;
      case EDIT_ALERT_CONFIRMATION:
        return `Verification "${alertConfirmationLabel}" was edited for alert "${alertLabel}" triggered on ${alertTimestamp}`;
      case REMOVE_ALERT_CONFIRMATION:
        return `Verification was withdrawn from alert "${alertLabel}" triggered on ${alertTimestamp}`;
      default:
        return 'Not recognized event';
    }
  } catch (e) {
    return 'Not recognized event';
  }
};
